/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef } from 'react';

// Utility to get initial value based on field type
const getInitialValue = field => {
  if (field.multiSelect) {
    return [];
  }
  return '';
};

const DependentFieldsHandler = ({ fields, watch, setValue }) => {
  // Flag to prevent recursive cascading clears
  const clearingInProgress = useRef(false);

  // Memoize the dependency map to avoid recalculation
  const { dependencyMap, fullDependencyGraph } = useMemo(() => {
    // Create direct dependency map
    const directDeps = fields.reduce((acc, field) => {
      const dependencies = Array.isArray(field.dependsOn)
        ? field.dependsOn
        : field.dependsOn
        ? [field.dependsOn]
        : null;

      if (dependencies) {
        dependencies.forEach(parentId => {
          if (!acc[parentId]) {
            acc[parentId] = new Set();
          }
          acc[parentId].add(field.id);
        });
      }
      return acc;
    }, {});

    // Pre-compute full dependency graph for each field
    const fullGraph = {};

    // Function to build the full dependency graph for a field
    const buildFullDependencyGraph = fieldId => {
      if (fullGraph[fieldId]) return fullGraph[fieldId];

      const dependents = new Set();
      const directDependents = directDeps[fieldId] || new Set();

      // Add direct dependents
      directDependents.forEach(depId => {
        dependents.add(depId);

        // Add indirect dependents (recursively)
        const indirectDeps = buildFullDependencyGraph(depId);
        indirectDeps.forEach(indirectDepId => {
          dependents.add(indirectDepId);
        });
      });

      fullGraph[fieldId] = dependents;
      return dependents;
    };

    // Build full dependency graph for each field
    Object.keys(directDeps).forEach(fieldId => {
      buildFullDependencyGraph(fieldId);
    });

    return { dependencyMap: directDeps, fullDependencyGraph: fullGraph };
  }, [fields]);

  // Optimization: Convert fields array to a map for faster lookup
  const fieldsMap = useMemo(() => {
    return fields.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});
  }, [fields]);

  // Clear dependent fields when parent changes
  const clearDependentFields = fieldId => {
    // Prevent recursive cascading clears
    if (clearingInProgress.current) return;

    try {
      clearingInProgress.current = true;

      // Get all dependent fields from pre-computed graph
      const dependents = Array.from(fullDependencyGraph[fieldId] || []);

      // Process each dependent field
      for (const depId of dependents) {
        const field = fieldsMap[depId];

        if (field) {
          // Check if field has multiple dependencies
          if (Array.isArray(field.dependsOn)) {
            // Only clear if all parent fields are empty
            const allParentsEmpty = field.dependsOn.every(parentId => {
              const parentValue = watch(parentId);
              return (
                !parentValue ||
                (Array.isArray(parentValue) && parentValue.length === 0)
              );
            });

            if (allParentsEmpty) {
              setValue(depId, getInitialValue(field));
            }
          } else {
            setValue(depId, getInitialValue(field));
          }
        }
      }
    } finally {
      // Always reset the flag when done
      clearingInProgress.current = false;
    }
  };

  // Watch all fields that have dependents
  const parentFields = useMemo(
    () => Object.keys(dependencyMap),
    [dependencyMap]
  );

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Only proceed if the changed field is a parent field
      if (parentFields.includes(name)) {
        // If field is cleared or changed
        if (
          !value[name] ||
          (Array.isArray(value[name]) && value[name].length === 0)
        ) {
          clearDependentFields(name);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, parentFields]);

  return null;
};

export default DependentFieldsHandler;
